import React, { useEffect, useMemo, useState, useRef } from 'react';
import { string, object, array, func } from 'prop-types';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import cx from 'classnames';

import useStyles from 'hooks/useStyles';

import {
  getGroupStatusBeginInitial,
  getAnimationGroupOffsets,
} from '../../utils';
import AnimationElementDesktop from '../AnimationElementDesktop';
import styles from './FeaturesDesktop.scss';

const firstFrameInitialFromTop = { opacity: 1, transform: `scale3d(1, 1, 1)` };
const stepOneInitialFromTop = { opacity: 0, transform: `scale3d(2.5, 2.5, 1)` };
const stepTwoInitialFromTop = { opacity: 0, transform: `scale3d(2.5, 2.5, 1)` };
const stepOneInitialFromBottom = {
  opacity: 0,
  transform: `scale3d(0.07, 0.07, 1)`,
};
const stepTwoInitialFromBottom = {
  opacity: 0,
  transform: `scale3d(0.155, 0.155, 1)`,
};
const stepThreeInitial = { bottom: 0, immediate: true };

const FeaturesDesktop = ({
  id,
  title,
  description,
  featuresConfig,
  inViewConfig,
  className,
  activeClassName,
}) => {
  useStyles(styles);
  const [firstFrameStyles, firstFrameApi] = useSpring(
    () => firstFrameInitialFromTop,
  );
  const [stepOneStyles, stepOneApi] = useSpring(() => stepOneInitialFromTop);
  const [stepTwoStyles, stepTwoApi] = useSpring(() => stepTwoInitialFromTop);
  const [stepThreeStyles, stepThreeApi] = useSpring(() => stepThreeInitial);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [hasInViewOnce, setHasInViewOnce] = useState(false);
  const animationElementCount = featuresConfig.length * 2;
  const [groupStatus, setGroupStatus] = useState(
    getGroupStatusBeginInitial(animationElementCount),
  );
  const lastPageYOffset = useRef(0);
  const { ref, inView, entry } = useInView();
  const [innerHeight, setInnerHeight] = useState(0);

  const animationGroupOffsets = useMemo(() => {
    const result = getAnimationGroupOffsets(
      innerHeight,
      animationElementCount,
      'desktop',
    );

    return result;
  }, [animationElementCount, innerHeight]);

  const groupStatusEndInitial = animationGroupOffsets.map(
    (item, index) => index === animationGroupOffsets.length - 1,
  );

  const resetFirstFrameAnimation = () => {
    firstFrameApi.start({
      ...firstFrameInitialFromTop,
      delay: 30,
      immediate: true,
    });
  };

  const resetStepOneAnimation = () => {
    stepOneApi.start({ ...stepOneInitialFromTop, delay: 30, immediate: true });
  };

  const resetStepTwoAnimation = () => {
    stepTwoApi.start({ ...stepTwoInitialFromTop, delay: 30, immediate: true });
  };

  const resetStepThreeAnimation = () => {
    stepThreeApi.start(stepThreeInitial);
  };

  const updateGroupStatue = (groupIndex, statue) => {
    setGroupStatus(currentGroupStatue => {
      const nextGroupStatus = [...currentGroupStatue];
      nextGroupStatus[groupIndex] = statue;
      return nextGroupStatus;
    });
  };

  useEffect(() => {
    setInnerHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    const handleOnScroll = () => {
      const currentPageYOffset = window.pageYOffset;
      const currentOffset = currentPageYOffset - entry?.target.offsetTop;
      const direction =
        currentPageYOffset > lastPageYOffset.current ? 'down' : 'up';
      lastPageYOffset.current = currentPageYOffset;
      let isStepOneActive = false;
      let isStepTwoActive = false;
      let isStepOneInitialState = false;
      let isStepTwoInitialState = false;

      animationGroupOffsets.forEach(({ begin, end }, index) => {
        // determine if the current offset is in the range of the current animation group
        if (currentOffset > begin && currentOffset <= end) {
          if (!groupStatus[index]) {
            updateGroupStatue(index, true);

            if (!hasInViewOnce && inViewConfig !== null) {
              const hasScrollToOffset =
                index ===
                animationGroupOffsets.length *
                  inViewConfig.offsetPercentage *
                  0.01;

              if (hasScrollToOffset) {
                inViewConfig.onInView(id, inViewConfig.offsetPercentage);
                setHasInViewOnce(true);
              }
            }
          }

          if (!shouldAnimate) {
            setShouldAnimate(true);
          }

          const currentGroupOffset = currentOffset - begin;
          // calculate the velocity of the current group
          const x = 1 - currentGroupOffset / innerHeight / 0.6;

          if (
            currentGroupOffset > 0 &&
            index !== animationGroupOffsets.length - 1
          ) {
            if (index === 0) {
              firstFrameApi.start({
                transform: `scale3d(${x},${x},1)`,
                opacity: x,
                zIndex: index + 1,
              });
            } else if (index % 2 === 0) {
              isStepOneActive = true;
              isStepOneInitialState = false;
              const scale = 2.5 * x;
              const opacity = scale < 0.5 ? 2 * x : 2 * (1 - x);

              stepOneApi.start({
                transform: `scale3d(${scale},${scale},1)`,
                opacity,
                zIndex: index + 1,
              });
            } else {
              isStepTwoActive = true;
              isStepTwoInitialState = false;
              const scale = 2.5 * x;
              const opacity = scale < 0.5 ? 2 * x : 2 * (1 - x);

              stepTwoApi.start({
                transform: `scale3d(${scale},${scale},1)`,
                opacity,
                zIndex: index + 2,
              });
            }
          } else if (index === animationGroupOffsets.length - 1) {
            stepTwoApi.start({
              transform: `scale3d(1,1,1)`,
              opacity: 1,
            });

            stepThreeApi.start({
              bottom: currentGroupOffset,
              immediate: true,
            });
            setGroupStatus(groupStatusEndInitial);
          }
        }

        if (currentOffset > end) {
          if (groupStatus[index]) {
            updateGroupStatue(index, false);
            if (direction === 'down') {
              if (index === 0) {
                resetFirstFrameAnimation();
              } else if (index % 2 === 0) {
                resetStepOneAnimation();
                isStepOneInitialState = true;
              } else {
                resetStepTwoAnimation();
                isStepTwoInitialState = true;
              }
            }
          }

          if (groupStatus[index] === false && direction === 'up') {
            if (index % 2 === 0 && !isStepOneActive && !isStepOneInitialState) {
              isStepOneInitialState = true;
              stepOneApi.start(stepOneInitialFromBottom);
            } else if (!isStepTwoActive && !isStepTwoInitialState) {
              isStepTwoInitialState = true;
              stepTwoApi.start(stepTwoInitialFromBottom);
            }
          }

          if (index === animationGroupOffsets.length - 1) {
            resetStepThreeAnimation();
          }
        }

        if (currentOffset < begin && groupStatus[index] === true) {
          if (index === 0) {
            resetFirstFrameAnimation();
          } else if (index % 2 === 0) {
            resetStepOneAnimation();
          } else {
            resetStepTwoAnimation();
          }
          resetStepThreeAnimation();

          updateGroupStatue(index, false);
        }
      });

      if (currentOffset < 0) {
        setGroupStatus(getGroupStatusBeginInitial(animationElementCount));
        setShouldAnimate(false);
      }
    };

    if (inView) {
      window.addEventListener('scroll', handleOnScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleOnScroll);
      if (!inView) {
        resetStepThreeAnimation();
        setShouldAnimate(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, groupStatus]);

  return (
    <section
      className={cx(styles.root, className)}
      ref={ref}
      id={id}
      style={{
        height: animationGroupOffsets[animationGroupOffsets.length - 1].end,
      }}
    >
      <div className={styles.wrapper} style={{ height: innerHeight }}>
        <animated.div
          style={stepThreeStyles}
          className={cx({
            [styles.groupActive]: shouldAnimate,
            [activeClassName]: shouldAnimate,
          })}
        >
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.description}>{description}</div>
          {featuresConfig.map(({ desktopType, ...props }, index) => (
            <AnimationElementDesktop
              firstGroupStyles={index === 0 ? firstFrameStyles : null}
              groupStatus={groupStatus.slice(index * 2, index * 4 + 2)}
              groupTwoStatus={groupStatus[index * 2 + 1]}
              groupOneStyles={stepOneStyles}
              groupTwoStyles={stepTwoStyles}
              key={props.title}
              type={desktopType}
              {...props}
            />
          ))}
        </animated.div>
      </div>
    </section>
  );
};

FeaturesDesktop.propTypes = {
  id: string,
  title: string.isRequired,
  description: string.isRequired,
  featuresConfig: array.isRequired,
  onScrollHalf: func,
  inViewConfig: object,
  className: string,
  activeClassName: string,
};

FeaturesDesktop.defaultProps = {
  id: '',
  onScrollHalf: null,
  inViewConfig: null,
  className: '',
  activeClassName: '',
};

export default FeaturesDesktop;
